import React, { useState } from "react";
import PropTypes from "prop-types";
import { Formik, Form } from "formik";
import dayjs from "dayjs";
import { ArrowBack as ArrowBackIcon } from "@material-ui/icons";
import {
  Grid,
  Radio,
  FormControlLabel,
  Box,
  MenuItem,
} from "@material-ui/core";
import translate from "i18n/translate";
import { DATE_QUERY_FORMAT } from "constants/dateFormats";
import { cieKeys } from "./cieKeys";
import { formModel } from "./formModel";
import { getValidationSchema } from "./getValidationSchema";
import { initialValues } from "./initialValues";
import useStyles from "../cashPaymentStyles";
import {
  SrButton,
  SrCurrencyInput,
  SrInput,
  SrDatePicker,
  Typography,
  SrAlert,
} from "../../../../common";
import { ImgWithFallback } from "../../../../ImgWithFallback";

const CreateBallot = ({
  onReturn,
  onSubmit: handleSubmit,
  isSubmitting,
  error,
  intl,
}) => {
  const classes = useStyles();
  const [cie, setCie] = useState(cieKeys[0].key);
  const [cieDesc, setCieDesc] = useState(cieKeys[0].desc);

  const { id: formId, fields } = formModel({ intl });
  const validationSchema = getValidationSchema({ intl }, cie);

  const handleCieChange = ({ target }) => {
    const key = target?.value;
    setCie(key);
    setCieDesc(cieKeys.filter((el) => el.key === key)?.[0].desc);
  };

  const onSubmit = async (form) => {
    const store = form.paymentDestination === 0 ? "oxxo" : "CIE";
    const base = {
      payment: {
        reference: {
          description: form.concept,
          email: form.email,
        },
      },
      total: parseFloat(form.amount),
      store,
    };
    const expirationDate = dayjs(
      form.validity || initialValues.validity
    ).format(DATE_QUERY_FORMAT);

    let body = {};

    if (store === "CIE") {
      const reference = cie !== cieKeys[0].key ? form.reference : undefined;
      const expiration = cie === cieKeys[1].key ? expirationDate : undefined;

      const bbvacie = {
        cie,
        reference,
        expiration,
      };
      body = { ...base, bbvacie };
    } else {
      body = {
        ...base,
        email: form.email,
        type: "barcode",
        expiration_date: expirationDate,
      };
    }

    handleSubmit(body);
  };

  return (
    <>
      <SrButton
        startIcon={<ArrowBackIcon color="primary" />}
        onClick={() => Promise.resolve().then(onReturn)}
      >
        <Typography variant="body1SemiBold">
          {intl.formatMessage({ id: "common.return" })}
        </Typography>
      </SrButton>
      <Box
        maxWidth={820}
        display="flex"
        flexDirection="column"
        margin="auto"
        marginTop={6}
      >
        <Grid container direction="column" spacing={3}>
          <Grid item>
            <Typography variant="h2" className={classes.formHeading}>
              {translate("cashPayment.createBallot.heading")}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="body1SemiBold">
              {translate("cashPayment.createBallot.p1")}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="h3SemiBold">
              {translate("cashPayment.createBallot.p2")}
            </Typography>
          </Grid>
        </Grid>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            touched,
            values,
            setFieldValue,
          }) => (
            <Form id={formId}>
              <Grid container spacing={2}>
                <Grid item xs={12} className={classes.typeSection}>
                  <Grid container className={classes.bankIcons}>
                    <Grid>
                      <FormControlLabel
                        checked={values.paymentDestination === 0}
                        label={
                          <ImgWithFallback
                            className={[
                              values.paymentDestination === 0
                                ? classes.OxxoSelected
                                : classes.OxxoImage,
                            ].join(" ")}
                            src="/banks/oxxo.webp"
                            fallback="/banks/oxxo.png"
                            alt={translate("cashPayment.createBallot.imageAlt")}
                          />
                        }
                        onChange={() => setFieldValue("paymentDestination", 0)}
                        control={<Radio className={classes.radio} />}
                        name="paymentDestination"
                      />
                    </Grid>
                    <Grid item>
                      <FormControlLabel
                        checked={values.paymentDestination === 1}
                        label={
                          <ImgWithFallback
                            className={[
                              values.paymentDestination === 1
                                ? classes.bbvaSelected
                                : classes.bbvaImage,
                            ].join(" ")}
                            src="/banks/bbva.webp"
                            fallback="/banks/bbva.png"
                            alt={translate("cashPayment.createBallot.imageAlt")}
                          />
                        }
                        onChange={() => setFieldValue("paymentDestination", 1)}
                        control={<Radio className={classes.radio} />}
                        name="paymentDestination"
                      />
                    </Grid>
                  </Grid>
                </Grid>
                {values.paymentDestination === 1 && (
                  <Grid container item xs={12}>
                    <Grid item xs={12} sm={6}>
                      <SrInput
                        select
                        fullWidth
                        labelId="ciekeys-select"
                        id="cie_key"
                        name="cie_key"
                        variant="outlined"
                        value={cie}
                        onChange={handleCieChange}
                      >
                        {cieKeys.map((item) => (
                          <MenuItem value={item.key} key={item.key}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </SrInput>
                    </Grid>
                    <Grid item xs={12} className={classes.cieDescription}>
                      <Typography>{cieDesc}</Typography>
                    </Grid>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <SrInput
                    label={fields.concept.label}
                    placeholder={fields.concept.label}
                    name={fields.concept.name}
                    type="text"
                    value={values.concept}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.concept && touched.concept)}
                    helperText={touched.concept && errors.concept}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <SrCurrencyInput
                    label={fields.amount.label}
                    placeholder={fields.amount.label}
                    name={fields.amount.name}
                    value={values.amount}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.amount && touched.amount)}
                    helperText={touched.amount && errors.amount}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <SrInput
                    label={fields.email.label}
                    placeholder={fields.email.label}
                    name={fields.email.name}
                    type="text"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.email && touched.email)}
                    helperText={touched.email && errors.email}
                    fullWidth
                  />
                </Grid>
                {(values.paymentDestination === 0 ||
                  (values.paymentDestination === 1 &&
                    cie === cieKeys[1].key)) && (
                  <Grid item xs={12}>
                    <SrDatePicker
                      id="date"
                      name={fields.validity.name}
                      label={fields.validity.label}
                      value={values.validity}
                      minDate={dayjs().add(1, "day")}
                      onChange={(value) =>
                        setFieldValue(fields.validity.name, value)
                      }
                      error={errors.validity && touched.validity}
                      helperText={touched.validity && errors.validity}
                      fullWidth
                    />
                  </Grid>
                )}
                {values.paymentDestination === 1 && (
                  <Grid item xs={12}>
                    {cie === cieKeys[1].key && (
                      <SrInput
                        label={fields.referenceNumeric.label}
                        placeholder={fields.referenceNumeric.placeholder}
                        name={fields.referenceNumeric.name}
                        type="text"
                        value={values.reference}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(errors.reference && touched.reference)}
                        helperText={touched.reference && errors.reference}
                        inputProps={{ maxLength: 7 }}
                        fullWidth
                      />
                    )}
                    {cie === cieKeys[2].key && (
                      <SrInput
                        label={fields.referenceAlphanumeric.label}
                        placeholder={fields.referenceAlphanumeric.placeholder}
                        name={fields.referenceAlphanumeric.name}
                        type="text"
                        value={values.reference}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(errors.reference && touched.reference)}
                        helperText={touched.reference && errors.reference}
                        inputProps={{ maxLength: 7 }}
                        fullWidth
                      />
                    )}
                  </Grid>
                )}
                {error && (
                  <Grid item xs={12}>
                    <SrAlert severity="error">{error}</SrAlert>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <SrButton
                    className={classes.submitButton}
                    variant="contained"
                    type="submit"
                    color="primary"
                    disabled={isSubmitting}
                  >
                    {translate("cashPayment.createBallot.sendBallot")}
                  </SrButton>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
        <Typography className={classes.bottomDescription}>
          {translate("cashPayment.createBallot.p3")}
        </Typography>
        <Typography className={classes.bottomDescription}>
          {translate("cashPayment.createBallot.p4")}
        </Typography>
      </Box>
    </>
  );
};

CreateBallot.propTypes = {
  onReturn: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  toastError: PropTypes.func.isRequired,
  error: PropTypes.string.isRequired,
};

export default CreateBallot;
